import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";
import { submitHohPilot } from "../lib/api";
import { trackFormAction } from "../lib/tracker";
import useRevealWhen from "../hooks/useRevealWhen";

// The founding pilot application (/heart-of-house/pilot/apply): the same questions as the Heart of House app's
// /founding-pilot form. Every field but the notes is required; the "other" answers open a short follow-up box.
// The program itself is HohPilotProgram (/heart-of-house/pilot).
const ROLES = ["owner", "gm", "chef", "other"];
const PLACE_TYPES = ["fine_dining", "casual", "fast_casual", "qsr", "bar", "cafe", "other"];
const COVERS = ["under_300", "300_700", "700_1500", "over_1500"];
const POS_SYSTEMS = ["square", "toast", "clover", "lightspeed", "micros", "genius", "touchbistro", "no_pos", "other"];
const COSTING = ["spreadsheets", "another_app", "pen_paper", "nothing_yet"];

const initialForm = {
  name: "",
  role: "",
  email: "",
  kitchen_name: "",
  city: "",
  place_type: "",
  place_type_other: "",
  covers: "",
  pos: "",
  pos_other: "",
  costing: "",
  costing_app: "",
  notes: "",
  updates: null,
};

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// [field, key of its required message]
const REQUIRED = [
  ["name", "name"],
  ["role", "role"],
  ["kitchen_name", "kitchen"],
  ["city", "city"],
  ["place_type", "placeType"],
  ["covers", "covers"],
  ["pos", "pos"],
  ["costing", "costing"],
];

const inputClass =
  "w-full bg-[#111111] border-0 border-b-2 border-[#3a3a3a] hover:border-[#5a5a5a] focus:border-[#E8490F] outline-none rounded-t-lg px-3.5 py-2.5 text-[15px] text-white placeholder:text-white/30 transition-colors aria-[invalid=true]:border-red-400/80";

const HohPilotForm = () => {
  const { t } = useLanguage();
  const p = t.hoh.pilotForm;
  const pg = t.hoh.pilotPage;
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const successRef = useRevealWhen(submitted);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = pg.metaTitle;
    return () => {
      document.title = prevTitle;
    };
  }, [pg.metaTitle]);

  const change = (name, value) => {
    if (form.name === "" && form.email === "" && form.kitchen_name === "") {
      trackFormAction("hoh-pilot:start");
    }
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => ({ ...e, [name]: null }));
  };

  const check = () => {
    const e = {};
    for (const [field, key] of REQUIRED) {
      if (!String(form[field] || "").trim()) e[field] = p.required[key];
    }
    if (!EMAIL_SHAPE.test(form.email.trim())) e.email = p.required.email;
    if (form.updates !== "yes" && form.updates !== "no") e.updates = p.required.updates;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting || !check()) return;
    setSubmitting(true);
    try {
      await submitHohPilot({
        name: form.name.trim(),
        role: form.role,
        email: form.email.trim(),
        restaurant_name: form.kitchen_name.trim(),
        city: form.city.trim(),
        place_type: form.place_type,
        place_type_other: form.place_type === "other" ? form.place_type_other.trim() || null : null,
        covers: form.covers,
        current_pos: form.pos,
        pos_other: form.pos === "other" ? form.pos_other.trim() || null : null,
        costing: form.costing,
        costing_app: form.costing === "another_app" ? form.costing_app.trim() || null : null,
        notes: form.notes.trim() || null,
        updates: form.updates,
        updates_question: p.updates.question,
        updates_answer: p.updates[form.updates],
      });
      trackFormAction("hoh-pilot:submit");
      setSubmitted(true);
      setForm(initialForm);
    } catch (err) {
      console.error(err);
      toast.error(p.errorGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  const options = (values, labels) => values.map((v) => [v, labels[v]]);

  return (
    <div data-testid="page-hoh-pilot-apply">
      <section className="relative overflow-hidden">
        <div
          className="pointer-events-none absolute left-1/2 -top-[460px] h-[620px] w-[1100px] -translate-x-1/2 rounded-[100%] bg-[#E8490F]/15 blur-3xl"
          aria-hidden
        />
        <div className="mise-container relative pt-10 md:pt-14 pb-16 md:pb-20">
          <Link
            to="/heart-of-house/pilot"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
            data-testid="hoh-pilot-back"
          >
            <ArrowLeft size={14} />
            {pg.backToProgram}
          </Link>

          <div className="mt-8 grid lg:grid-cols-12 gap-8 lg:gap-12">
            <aside className="lg:col-span-4 min-w-0">
              <div className="lg:sticky lg:top-28">
                <p className="mise-eyebrow">{pg.formEyebrow}</p>
                <h1 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-white leading-[1.08]">
                  {pg.formHeading}
                </h1>
                <p className="mt-4 text-white/65 leading-relaxed">{pg.formSub}</p>

                <div className="mt-8 rounded-2xl border border-[#333333] bg-[#181818] p-6" data-testid="hoh-pilot-sean">
                  <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#E8490F]">{pg.seanEyebrow}</p>
                  <p className="mt-2.5 font-semibold text-white">{pg.seanName}</p>
                  <ul className="mt-3 flex flex-wrap gap-1.5">
                    {pg.seanCreds.map((c) => (
                      <li key={c} className="rounded-full border border-[#3a3a3a] px-3 py-1 text-xs text-white/70">
                        {c}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4 text-sm text-white/60 leading-relaxed">{pg.seanText}</p>
                  <a
                    href={`mailto:${pg.seanEmail}`}
                    className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-white hover:text-[#E8490F] transition-colors"
                    data-track="hoh-pilot-email-sean-card"
                  >
                    <Mail size={15} className="text-[#E8490F]" />
                    {pg.seanEmail}
                  </a>
                </div>
              </div>
            </aside>

            <div className="lg:col-span-8 min-w-0">
              <div className="relative overflow-hidden rounded-2xl border border-[#333333] bg-[#1a1a1a]">
                <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#E8490F] via-[#ff7a3d] to-[#E8490F]" aria-hidden />
                {submitted ? (
                  <div
                    className="p-10 md:p-16 text-center outline-none"
                    data-testid="hoh-pilot-success"
                    ref={successRef}
                    tabIndex={-1}
                    role="status"
                  >
                    <CheckCircle2 className="mx-auto text-[#E8490F]" size={52} />
                    <p className="mt-6 text-lg text-white/85 leading-relaxed max-w-xl mx-auto">{p.success}</p>
                    <Link to="/heart-of-house" className="mt-8 inline-flex mise-pill-secondary" data-testid="hoh-pilot-return">
                      <ArrowLeft size={16} />
                      {p.returnCta}
                    </Link>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} noValidate className="p-6 sm:p-7 md:p-8" data-testid="hoh-pilot-form">
                    <FormSection n={1} title={pg.formSections[0]}>
                      <TextField id="pilot-name" label={p.labels.name} value={form.name} autoComplete="name"
                        onChange={(v) => change("name", v)} error={errors.name} />
                      <SelectField id="pilot-role" label={p.labels.role} value={form.role} placeholder={p.pickOne}
                        options={options(ROLES, p.roles)} onChange={(v) => change("role", v)} error={errors.role} />
                      <div className="sm:col-span-2">
                        <TextField id="pilot-email" type="email" label={p.labels.email} value={form.email}
                          autoComplete="email" onChange={(v) => change("email", v)} error={errors.email} />
                      </div>
                    </FormSection>

                    <FormSection n={2} title={pg.formSections[1]}>
                      <TextField id="pilot-kitchen" label={p.labels.kitchen} value={form.kitchen_name}
                        autoComplete="organization" onChange={(v) => change("kitchen_name", v)} error={errors.kitchen_name} />
                      <TextField id="pilot-city" label={p.labels.city} value={form.city} autoComplete="address-level2"
                        onChange={(v) => change("city", v)} error={errors.city} />
                      <SelectField id="pilot-place-type" label={p.labels.placeType} value={form.place_type}
                        placeholder={p.pickOne} options={options(PLACE_TYPES, p.placeTypes)}
                        onChange={(v) => change("place_type", v)} error={errors.place_type}
                        other={form.place_type === "other" && (
                          <OtherInput label={p.labels.placeTypeOther} value={form.place_type_other}
                            testId="pilot-place-type-other" onChange={(v) => change("place_type_other", v)} />
                        )} />
                      <SelectField id="pilot-covers" label={p.labels.covers} value={form.covers} placeholder={p.pickOne}
                        options={options(COVERS, p.coversOptions)} onChange={(v) => change("covers", v)}
                        error={errors.covers} />
                    </FormSection>

                    <FormSection n={3} title={pg.formSections[2]} last>
                      <SelectField id="pilot-pos" label={p.labels.pos} value={form.pos} placeholder={p.pickOne}
                        options={options(POS_SYSTEMS, p.posOptions)} onChange={(v) => change("pos", v)} error={errors.pos}
                        other={form.pos === "other" && (
                          <OtherInput label={p.labels.posOther} value={form.pos_other} testId="pilot-pos-other"
                            onChange={(v) => change("pos_other", v)} />
                        )} />
                      <SelectField id="pilot-costing" label={p.labels.costing} value={form.costing}
                        placeholder={p.pickOne} options={options(COSTING, p.costingOptions)}
                        onChange={(v) => change("costing", v)} error={errors.costing}
                        other={form.costing === "another_app" && (
                          <OtherInput label={p.labels.costingApp} value={form.costing_app} testId="pilot-costing-app"
                            onChange={(v) => change("costing_app", v)} />
                        )} />
                      <div className="sm:col-span-2 grid gap-2">
                        <label className="text-sm text-white/60" htmlFor="pilot-notes">
                          {p.labels.notes} <span className="text-white/40">{p.optional}</span>
                        </label>
                        <textarea
                          id="pilot-notes"
                          rows={3}
                          maxLength={2000}
                          value={form.notes}
                          onChange={(e) => change("notes", e.target.value)}
                          className={`${inputClass} leading-relaxed`}
                          data-testid="pilot-notes"
                        />
                      </div>
                    </FormSection>

                    <div
                      className="mt-6 rounded-xl bg-[#111111] p-4"
                      role="radiogroup"
                      aria-labelledby="pilot-updates-label"
                      aria-required="true"
                      aria-describedby={errors.updates ? "pilot-updates-error" : undefined}
                      data-testid="pilot-updates"
                    >
                      <p id="pilot-updates-label" className="text-sm text-white/75">
                        {p.updates.question}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {["yes", "no"].map((v) => {
                          const on = form.updates === v;
                          return (
                            <button
                              key={v}
                              type="button"
                              role="radio"
                              aria-checked={on}
                              onClick={() => change("updates", v)}
                              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                                on
                                  ? "border-[#E8490F] bg-[#E8490F] text-white"
                                  : "border-[#3a3a3a] text-white/75 hover:border-[#6a6a6a] hover:text-white"
                              }`}
                              data-testid={`pilot-updates-${v}`}
                            >
                              <span
                                className={`h-3.5 w-3.5 rounded-full border-2 ${on ? "border-white bg-white" : "border-white/40"}`}
                                aria-hidden
                              />
                              {p.updates[v]}
                            </button>
                          );
                        })}
                      </div>
                      <FieldError id="pilot-updates-error">{errors.updates}</FieldError>
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#E8490F] px-6 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-[#d1410d] disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid="hoh-pilot-submit"
                    >
                      {submitting ? p.sending : p.submit}
                      {!submitting && <ArrowRight size={18} />}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

const FormSection = ({ n, title, last, children }) => (
  <fieldset className={`grid gap-5 ${last ? "" : "pb-6 mb-6 border-b border-[#2a2a2a]"}`}>
    <legend className="mb-4 flex items-center gap-3">
      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-[#E8490F]/15 text-xs font-bold text-[#E8490F]">
        0{n}
      </span>
      <span className="text-sm font-semibold tracking-[0.18em] uppercase text-white/80">{title}</span>
    </legend>
    <div className="grid sm:grid-cols-2 gap-x-5 gap-y-4">{children}</div>
  </fieldset>
);

const FieldError = ({ id, children }) =>
  children ? (
    <p id={id} className="mt-1 text-xs text-red-300" role="alert">
      {children}
    </p>
  ) : null;

const TextField = ({ id, label, value, onChange, error, type = "text", autoComplete }) => (
  <div className="grid gap-2 content-start min-w-0">
    <label className="text-sm text-white/60" htmlFor={id}>
      {label}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      required
      autoComplete={autoComplete}
      onChange={(e) => onChange(e.target.value)}
      aria-invalid={Boolean(error)}
      aria-describedby={error ? `${id}-error` : undefined}
      className={inputClass}
      data-testid={id}
    />
    <FieldError id={`${id}-error`}>{error}</FieldError>
  </div>
);

const SelectField = ({ id, label, value, onChange, placeholder, options, error, other }) => (
  <div className="grid gap-2 content-start min-w-0">
    <label className="text-sm text-white/60" htmlFor={id}>
      {label}
    </label>
    <select
      id={id}
      value={value}
      required
      onChange={(e) => onChange(e.target.value)}
      aria-invalid={Boolean(error)}
      aria-describedby={error ? `${id}-error` : undefined}
      className={`${inputClass} ${value ? "" : "text-white/40"}`}
      data-testid={id}
    >
      <option value="" disabled>
        {placeholder}
      </option>
      {options.map(([v, text]) => (
        <option key={v} value={v} className="text-white">
          {text}
        </option>
      ))}
    </select>
    <FieldError id={`${id}-error`}>{error}</FieldError>
    {other}
  </div>
);

const OtherInput = ({ label, value, onChange, testId }) => (
  <input
    aria-label={label}
    placeholder={label}
    maxLength={120}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={inputClass}
    data-testid={testId}
  />
);

export default HohPilotForm;
